import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreedingMethod } from '../../shared/breeding-method/model/breeding-method';
import { BreedingMethodSearchRequest } from '../../shared/breeding-method/model/breeding-method-search-request.model';
import { SearchResult } from '../../shared/search-result.model';
import { Router } from '@angular/router';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { AlertService } from '../../shared/alert/alert.service';
import { BreedingMethodService } from '../../shared/breeding-method/service/breeding-method.service';
import { ColumnFilterComponent, FilterType } from '../../shared/column-filter/column-filter.component';
import { LocationSearchRequest } from '../../shared/location/model/location-search-request.model';
import { finalize } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { MatchType } from '../../shared/column-filter/column-filter-text-with-match-options-component';
import { BreedingMethodType } from '../../shared/breeding-method/model/breeding-method-type.model';
import { BreedingMethodGroup } from '../../shared/breeding-method/model/breeding-method-group.model';
import { BreedingMethodClass } from '../../shared/breeding-method/model/breeding-method-class.model';
import { ColumnFilterTransitionEventModel } from '../../shared/column-filter/column-filter-transition-event.model';
import { Location } from '../../shared/location/model/location';
import { Pageable } from '../../shared/model/pageable';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CropSettingsContext } from '../crop-Settings.context';
import { TranslateService } from '@ngx-translate/core';
import { GermplasmService } from '../../shared/germplasm/service/germplasm.service';
import { NameType } from '../../shared/germplasm/model/name-type.model';
import { DropdownOptionData } from '../../shared/column-filter/column-filter-dropdown-option-data';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-breeding-methods-pane',
    templateUrl: 'breeding-methods-pane.component.html',
    styleUrls: [
        './breeding-methods-pane.component.scss'
    ]
})
export class BreedingMethodsPaneComponent implements OnInit, OnDestroy {

    static readonly COLUMN_FILTER_EVENT_NAME = 'searchColumnFiltersChanged';

    COLUMN_FILTER_EVENT_NAME = BreedingMethodsPaneComponent.COLUMN_FILTER_EVENT_NAME;

    ColumnLabels = ColumnLabels;

    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();
    breedingMethods: BreedingMethod[];

    currentSearch: string;

    itemsPerPage = 20;
    page: number;
    previousPage: number;
    predicate: any;
    reverse: boolean;
    resultSearch: SearchResult;

    isLoading: boolean;

    searchRequest: BreedingMethodSearchRequest;

    totalItems: number;
    breedingMethodFilters: any;

    constructor(public translateService: TranslateService,
                private jhiLanguageService: JhiLanguageService,
                private eventManager: JhiEventManager,
                private breedingMethodService: BreedingMethodService,
                private germplasmService: GermplasmService,
                private router: Router,
                private alertService: AlertService,
                private modalService: NgbModal,
                private cropSettingsContext: CropSettingsContext
    ) {
        this.page = 1;
        this.totalItems = 0;
        this.currentSearch = '';
        this.predicate = [ColumnLabels.NAME];
        this.reverse = false;
        this.resultSearch = new SearchResult('');
        this.searchRequest = new BreedingMethodSearchRequest();
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        this.filters = this.getInitialFilters();
        this.eventManager.broadcast({ name: 'reloadFilters', content: { filters: this.filters, request: this.request } });
        this.registerColumnFiltersChanged();
        this.loadAll(this.request);
        this.registerBreedingMethodsChanged();
    }

    get request() {
        return this.searchRequest;
    }

    set request(request: LocationSearchRequest) {
        this.searchRequest = request;
    }

    get filters() {
        return this.breedingMethodFilters;
    }

    set filters(filters) {
        this.breedingMethodFilters = filters;
    }

    loadAll(request: BreedingMethodSearchRequest) {
        this.isLoading = true;
        this.breedingMethodService.searchBreedingMethods(request, false,
            <Pageable>({
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            })
        ).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<BreedingMethod[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.loadAll(this.request);
        }
    }

    sort() {
        this.page = 1;
        this.loadAll(this.request);
    }

    private getSort() {
        if (!this.predicate) {
            return '';
        }

        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    private clearSort() {
        this.predicate = [ColumnLabels.NAME];
        this.reverse = false;
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.loadAll(this.request);
    }

    trackId(item: Location) {
        return item.id;
    }

    private registerColumnFiltersChanged() {
        this.subscriptionsManager.add = this.eventManager.subscribe(BreedingMethodsPaneComponent.COLUMN_FILTER_EVENT_NAME, (event: ColumnFilterTransitionEventModel) => {
            this.resetTable();
        });
    }

    async loadBreedingMethod() {
        this.loadAll(this.request);
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.loadAll(this.request);
    }

    registerBreedingMethodsChanged(): any {
        this.subscriptionsManager.add = this.eventManager.subscribe('breedingMethodViewChanged', (event) => {
            this.loadAll(this.request);
        });
    }

    private getInitialFilters() {
        const me = this;

        return [
            {
                key: 'nameFilter', name: 'column-filter.method.name', placeholder: 'column-filter.search.text.placeholder', type: FilterType.TEXT_WITH_MATCH_OPTIONS,
                matchType: MatchType.STARTSWITH, default: true
            },
            { key: 'description', name: 'column-filter.description', placeholder: 'column-filter.contains.text.placeholder', type: FilterType.TEXT },
            {
                key: 'groups', name: 'column-filter.group', type: FilterType.DROPDOWN, values: this.getBreedingMethodGroupsOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            },
            { key: 'methodAbbreviations', name: 'column-filter.code', placeholder: 'column-filter.match.text.placeholder', type: FilterType.TEXT,
                transform(req) {
                    req[this.key] = [this.value];
                }
            },
            {
                key: 'methodTypes', name: 'column-filter.type', type: FilterType.DROPDOWN, values: this.getBreedingMethodTypesOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            },
            {
                key: 'methodDate', name: 'column-filter.date', type: FilterType.DATE,
                fromKey: 'methodDateFrom',
                toKey: 'methodDateTo',
                transform(req) {
                    ColumnFilterComponent.transformDateFilter(this, req, this.fromKey, this.toKey);
                },
                reset(req) {
                    ColumnFilterComponent.resetRangeFilter(this, req, this.fromKey, this.toKey);
                },
                reload(req) {
                    this.from = req[this.fromKey];
                    this.to = req[this.toKey];
                }
            },
            {
                key: 'methodClassIds', name: 'column-filter.class', type: FilterType.DROPDOWN, values: this.getBreedingMethodClassOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            },
            {
                key: 'snameTypeIds', name: 'column-filter.source.name.types', type: FilterType.DROPDOWN, values: this.getNameTypeOptions(), multipleSelect: true,
                transform(req) {
                    ColumnFilterComponent.transformDropdownFilter(this, req);
                },
                reset(req) {
                    ColumnFilterComponent.resetDropdownFilter(this, req);
                },
            }
        ];
    }

    private getBreedingMethodTypesOptions(): Promise<DropdownOptionData[]> {
        return this.breedingMethodService.queryBreedingMethodTypes().toPromise().then((types: BreedingMethodType[]) => {
            return types.map((type: BreedingMethodType) => {
                return { id: type.code,
                    text: type.name + ' (' + type.code + ')'
                };
            });
        });
    }

    private getBreedingMethodGroupsOptions(): Promise<DropdownOptionData[]> {
        return this.breedingMethodService.queryBreedingMethodGroups().toPromise().then((groups: BreedingMethodGroup[]) => {
            return groups.map((group: BreedingMethodGroup) => {
                return { id: group.code,
                    text: group.name + ' (' + group.code + ')'
                };
            });
        });
    }

    private getBreedingMethodClassOptions(): Promise<DropdownOptionData[]> {
        return this.breedingMethodService.queryBreedingMethodClasses().toPromise().then((classes: BreedingMethodClass[]) => {
            return classes.map((clazz: BreedingMethodClass) => {
                return { id: clazz.id.toString(),
                    text: clazz.name
                };
            });
        });
    }

    private getNameTypeOptions(): Promise<DropdownOptionData[]> {
        return this.germplasmService.getGermplasmNameTypes([]).toPromise().then((nameTypes: NameType[]) => {
            return nameTypes.map((nameType: NameType) => {
                return { id: nameType.id.toString(),
                    text: nameType.code
                };
            });
        });
    }

    private onSuccess(data: BreedingMethod[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.breedingMethods = data;
    }

    editBreedingMethod(breedingMethod: any) {
        this.cropSettingsContext.breedingMethod = breedingMethod;
        this.router.navigate(['/', { outlets: { popup: 'breeding-method-edit-dialog' }, }], { queryParamsHandling: 'merge' });

    }

    deleteBreedingMethod(breedingMethod: any) {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('crop-settings-manager.confirmation.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('crop-settings-manager.breeding-method.modal.delete.warning', { param: breedingMethod.name });
        confirmModalRef.result.then(() => {
            this.breedingMethodService.deleteBreedingMethod(breedingMethod.mid).toPromise().then((result) => {
                this.alertService.success('crop-settings-manager.breeding-method.modal.delete.success');
                this.loadBreedingMethod();
            });
        }, () => confirmModalRef.dismiss());
    }

}

export enum ColumnLabels {
    'NAME' = 'NAME',
    'DESCRIPTION' = 'DESCRIPTION',
    'GROUP' = 'GROUP',
    'CODE' = 'CODE',
    'TYPE' = 'TYPE',
    'DATE' = 'DATE',
    'CLASS_NAME' = 'CLASS_NAME',
    'SNAME_TYPE_CODE' = 'SNAME_TYPE_CODE'
}
